import React, { useState, useMemo } from 'react';
import { css } from 'twin.macro';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Form, Row, Col } from 'react-bootstrap';
import { BsTrash, BsGripVertical } from 'react-icons/bs';
import { defaultFormItems } from '../../FormPicker/DefaultFormElements';
import FormElementEdit from './FormElementEdit';
import ID from '@/_helpers/uuid';
import Select from 'react-select';
import { generateObjectId } from '@/_helpers';

const FieldTypeSelect = ({ availableFieldTypes, onAddField }) => {
    const [selectedValue, setSelectedValue] = useState(null);
    return (
        <div className="mb-3 d-flex justify-content-end">
            <Select
                value={selectedValue}
                options={availableFieldTypes.map((fieldType) => ({
                    value: fieldType.key,
                    label: (
                        <div
                            className="d-flex flex-row align-items-center"
                            css={css`
                                color: #17a2b8;
                            `}
                        >
                            <span className="mr-2">{fieldType.icon}</span>
                            <span>{fieldType.key}</span>
                        </div>
                    ),
                }))}
                placeholder="+ Add field"
                onChange={(selectedOption) => {
                    if (selectedOption) {
                        onAddField(selectedOption.value);
                        setSelectedValue(null);
                    }
                }}
                className="field-type-select"
                styles={{
                    control: (base, state) => ({
                        ...base,
                        width: '250px',
                        padding: '4px 8px',
                        fontSize: '16px',
                        boxShadow: state.isFocused ? '0 0 0 1px #50aeb0' : base.boxShadow,
                        borderColor: '#50aeb0',
                        '&:hover': {
                            borderColor: state.isFocused ? '#50aeb0' : '#ccc',
                        },
                    }),
                    menu: (base) => ({
                        ...base,
                        zIndex: 9999,
                    }),
                    option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isFocused ? '#50aeb040' : base.backgroundColor,
                        color: '#000',
                    }),
                    dropdownIndicator: (base, state) => ({
                        ...base,
                        color: '#50aeb0',
                    }),
                    indicatorSeparator: (base, state) => ({
                        ...base,
                        backgroundColor: state.isFocused ? '#50aeb075' : base.backgroundColor,
                    }),
                }}
            />
        </div>
    );
};
const ConfigurableListFieldsSelect = React.memo(({ register, element, setOptions: setFields, options: fields }) => {
    const availableFieldTypes = useMemo(() => {
        const allowedTypes = [
            'Email',
            'InputField',
            'NumberField',
            'Select',
            'Checkbox',
            'Radio',
            'DatePicker',
            'TimePicker',
            'DateTimePicker',
            'FullName',
        ];

        return defaultFormItems()
            .filter((item) => allowedTypes.includes(item.key))
            .map((item) => ({
                ...item,
                data: item?.options || item?.data,
            }));
    }, []);

    const handleDragEnd = (result) => {
        if (!result.destination) return;
        const reorderedFields = Array.from(fields);
        const [movedField] = reorderedFields.splice(result.source.index, 1);
        reorderedFields.splice(result.destination.index, 0, movedField);
        setFields(reorderedFields);
    };

    const handleAddField = (fieldType = null) => {
        if (!fieldType) {
            return;
        }

        const selectedFieldType = availableFieldTypes.find((ft) => ft.key === fieldType);
        const id = generateObjectId();
        const newField = {
            _id: id,
            id: id,
            type: selectedFieldType?.type,
            key: selectedFieldType?.key,
            instruction: selectedFieldType?.instruction || '',
            fieldName: selectedFieldType?.label || selectedFieldType.fieldName,
            required: false,
            data: ['Select', 'Checkbox', 'Radio'].includes(fieldType)
                ? [
                      { text: 'option 1', value: ID.uuid().replace('-', '') },
                      { text: 'option 2', value: ID.uuid().replace('-', '') },
                      { text: 'option 3', value: ID.uuid().replace('-', '') },
                  ]
                : selectedFieldType?.data || [],
        };
        setFields((prev) => [...prev, newField]);
    };

    const handleRemoveField = (fieldId) => {
        setFields((prev) => prev.filter((field) => field._id !== fieldId));
    };
    const handleEditField = (data) => {
        setFields((prev) => prev.map((field) => (field._id === data._id ? data : field)));
    };
    return (
        <div className="configurable-list">
            <div className="border-bottom pb-3 mb-3">
                <Row>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Minimum Rows</Form.Label>
                            <Form.Control
                                type="number"
                                placeholder=""
                                min={1}
                                defaultValue={(element?.fieldProperties || {})?.minRows || 1}
                                name="fieldProperties.minRows"
                                ref={register}
                                onWheel={(event) => event.currentTarget.blur()}
                                style={{ width: '300px' }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Maximum Rows</Form.Label>
                            <Form.Control
                                type="number"
                                placeholder=""
                                min={(element?.fieldProperties || {})?.minRows || 0}
                                defaultValue={(element?.fieldProperties || {})?.maxRows || 10}
                                name="fieldProperties.maxRows"
                                ref={register}
                                onWheel={(event) => event.currentTarget.blur()}
                                style={{ width: '300px' }}
                            />
                        </Form.Group>
                    </Col>
                </Row>
            </div>

            {/* Fields List */}
            <div className="mb-3">
                <FieldTypeSelect availableFieldTypes={availableFieldTypes} onAddField={handleAddField} />

                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="configurable-fields">
                        {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef}>
                                {fields.map((field, index) => (
                                    <Draggable key={field._id} draggableId={field._id} index={index}>
                                        {(provided, snapshot) => (
                                            <div
                                                className="d-flex mb-2"
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                            >
                                                <div
                                                    {...provided.dragHandleProps}
                                                    css={css`
                                                        cursor: grab;
                                                        display: flex;
                                                        align-items: center;
                                                        justify-content: center;
                                                    `}
                                                >
                                                    <BsGripVertical size={24} />
                                                </div>

                                                <div
                                                    css={css`
                                                        display: flex;
                                                        flex: 1;
                                                        padding: 2px;
                                                        cursor: pointer;
                                                        margin-left: 8px;
                                                        display: flex;
                                                        align-items: center;
                                                        gap: 8px;
                                                        color: #17a2b8;
                                                    `}
                                                    style={{
                                                        border: '1px solid #17a2b8',
                                                        padding: '5px',
                                                    }}
                                                >
                                                    {availableFieldTypes.find((ft) => ft.key === field.key)?.icon}
                                                    {field.key}:{' '}
                                                    <span
                                                        css={css`
                                                            color: #000;
                                                        `}
                                                    >
                                                        {field.fieldName || field.name}
                                                    </span>
                                                </div>
                                                <div
                                                    css={css`
                                                        padding: 2px;
                                                        cursor: pointer;
                                                        margin-left: 8px;
                                                        display: flex;
                                                        align-items: center;
                                                        gap: 8px;
                                                    `}
                                                >
                                                    <FormElementEdit element={field} onEdit={handleEditField} />

                                                    {fields.length > 1 && (
                                                        <div>
                                                            <BsTrash
                                                                color="red"
                                                                onClick={() => handleRemoveField(field._id)}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>
        </div>
    );
});

export default ConfigurableListFieldsSelect;
